import { useQuery } from '@tanstack/react-query';

import { getGeneralWebsiteConfig, getPublicSubModule, getPublicModule, getPrivateSubModule } from '../../Services/APIs/LandingPage';

import { useAuth } from '../../hoc/AuthContext';

import { isValidSubvariant } from "../../Utils/CommonFunctions.js";

export const useIntegratorTheme = (isSubdomain, newTheme) => {
  const { setIntegratorTheme } = useAuth();

  const { data: integratorTheme, isLoading: isIntegratorThemeLoading } = useQuery({
    queryKey: ['integrator-theme', isSubdomain],
    queryFn: async () => {
      const result = await getPublicSubModule("");
      if (result.status !== 200 || !result.data) {
        throw new Error(`Invalid submodule config: ${JSON.stringify(result)}`);
      }
      setIntegratorTheme(result?.data);
      return result?.data;
    },
    enabled: !!isSubdomain,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 1,
    initialData: !isSubdomain ? newTheme : undefined,
    onSuccess: (data) => setIntegratorTheme(data),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return { integratorTheme, isIntegratorThemeLoading };
};

export const HeaderWebsiteConfig = () => {
  const { setWebsiteInfo, setHeaderData } = useAuth();
  const { data: headerConfigData, isLoading: isConfigLoading } = useQuery({
    queryKey: ["header-website-config"],
    queryFn: async () => {
      const param = `pages=WebsiteConfig,Header`;
      const result = await getGeneralWebsiteConfig(param);
      if (result.status !== 200 || !result.data || typeof result.data !== 'object') {
        throw new Error(`Invalid config data: ${JSON.stringify(result)}`);
      }
      return result.data;
    },
    onSuccess: (data) => {
      setWebsiteInfo(data.WebsiteConfig || {});
      setHeaderData(data.Header || {});
    },
    onError: (error) => {
      console.error('Config API Error:', error.message);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return { headerConfigData, isConfigLoading };
};

export const HeaderModuleInfo = (isPublic, subId, usePublicIntegratorModules = false) => {
  const { setAllSubModuleInfo, isAuthenticated, subVarientIdGlobal } = useAuth();
  const id = usePublicIntegratorModules ? null : (isPublic ? subId : subVarientIdGlobal);

  // enable when valid card id and for public and private authenticated page
  const isEnabled = usePublicIntegratorModules
    ? true
    : isValidSubvariant(id) && (isPublic || isAuthenticated);

  const { data: headerSubModuleData } = useQuery({
    queryKey: ['sub-module', isAuthenticated, subVarientIdGlobal, id, usePublicIntegratorModules],
    queryFn: async () => {
      // Prevent API call if id is not valid (e.g., 0, null, undefined)
      if (!isEnabled) return undefined;
      const result = usePublicIntegratorModules
        ? await getPublicSubModule("modules=true")
        : isPublic ? await getPublicModule(`card_id=${id}`) : await getPrivateSubModule(`card_id=${id}`);
      if (result?.status !== 200) {
        throw new Error(`SubModule API failed: ${JSON.stringify(result)}`);
      }
      return usePublicIntegratorModules ? result.data.modules : result.data;
    },
    onSuccess: (data) => {
      setAllSubModuleInfo(data);
    },
    onError: (error) => {
      console.error('SubModule API Error:', error.message);
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: isEnabled, // This disables the query if id is not valid or not allowed
  });

  return { headerSubModuleData };
};
